import { useState } from 'react';
import {
  Newspaper,
  Loader2,
  AlertCircle,
  Plus,
  Star,
  Send,
  Archive,
  Trash2,
} from 'lucide-react';
import { PostingStatus } from '@pms/shared';
import type { Posting } from '@pms/shared';
import {
  usePostings,
  useCreatePosting,
  useUpdatePosting,
  useDeletePosting,
  usePublishPosting,
  useArchivePosting,
} from '@/hooks/api/useContent';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { Button } from '@/components/ui/Button';
import { extractErrorMessage } from '@/lib/error';
import { cn } from '@/lib/utils';

const STATUS_TABS: { value: PostingStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: PostingStatus.DRAFT, label: 'Draft' },
  { value: PostingStatus.PUBLISHED, label: 'Published' },
  { value: PostingStatus.ARCHIVED, label: 'Archived' },
];

const STATUS_BADGE: Record<PostingStatus, string> = {
  [PostingStatus.DRAFT]: 'bg-gray-100 text-gray-600',
  [PostingStatus.PUBLISHED]: 'bg-green-100 text-green-700',
  [PostingStatus.ARCHIVED]: 'bg-amber-100 text-amber-800',
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ── Editor ──────────────────────────────────────────────────────────────────

function PostingEditor({
  posting,
  onClose,
}: {
  posting: Posting | null;
  onClose: () => void;
}) {
  const isEdit = !!posting;
  const create = useCreatePosting();
  const update = useUpdatePosting();
  const [title, setTitle] = useState(posting?.title ?? '');
  const [slug, setSlug] = useState(posting?.slug ?? '');
  const [body, setBody] = useState(posting?.body ?? '');
  const [heroImageUrl, setHeroImageUrl] = useState(posting?.heroImageUrl ?? '');
  const [featured, setFeatured] = useState(posting?.featured ?? false);
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;

  function handleTitle(value: string) {
    setTitle(value);
    if (!slugTouched) setSlug(slugify(value));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const payload = {
      title: title.trim(),
      slug: slug.trim(),
      body: body.trim() || undefined,
      heroImageUrl: heroImageUrl.trim() || undefined,
      featured,
    };
    const onError = (err: unknown) => setError(extractErrorMessage(err));
    if (isEdit && posting) {
      update.mutate({ id: posting.id, patch: payload }, { onSuccess: onClose, onError });
    } else {
      create.mutate(payload, { onSuccess: onClose, onError });
    }
  }

  return (
    <SlideOver isOpen onClose={onClose} title={isEdit ? 'Edit posting' : 'New posting'} width="max-w-xl">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="p-title" className="block text-sm font-semibold text-gray-900 mb-1.5">
            Title
          </label>
          <input
            id="p-title"
            required
            value={title}
            onChange={(e) => handleTitle(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <div>
          <label htmlFor="p-slug" className="block text-sm font-semibold text-gray-900 mb-1.5">
            Slug
          </label>
          <input
            id="p-slug"
            required
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <p className="mt-1 text-xs text-gray-400">Lowercase words separated by hyphens.</p>
        </div>
        <div>
          <label htmlFor="p-hero" className="block text-sm font-semibold text-gray-900 mb-1.5">
            Hero image URL (optional)
          </label>
          <input
            id="p-hero"
            type="url"
            value={heroImageUrl}
            onChange={(e) => setHeroImageUrl(e.target.value)}
            placeholder="https://…"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <div>
          <label htmlFor="p-body" className="block text-sm font-semibold text-gray-900 mb-1.5">
            Body (optional)
          </label>
          <textarea
            id="p-body"
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={featured}
            onChange={(e) => setFeatured(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Featured
        </label>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={pending || !title.trim() || !slug.trim()}>
            {pending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {isEdit ? 'Save' : 'Create'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function AdminPostingsPage() {
  const [status, setStatus] = useState<PostingStatus | 'all'>('all');
  const [editing, setEditing] = useState<Posting | null>(null);
  const [creating, setCreating] = useState(false);
  const { data, isLoading, isError, refetch } = usePostings(status);

  const publish = usePublishPosting();
  const archive = useArchivePosting();
  const remove = useDeletePosting();
  const update = useUpdatePosting();

  const postings = data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Postings & Featured Content"
        description="Editorial postings surfaced on the BestAcco storefront."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus size={16} aria-hidden="true" /> New posting
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Posting status filter">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={status === tab.value}
            onClick={() => setStatus(tab.value)}
            className={cn(
              'px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              status === tab.value
                ? 'bg-brand-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <AlertCircle size={24} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
            <p className="text-sm text-red-700 mb-4">Unable to load postings.</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : postings.length === 0 ? (
          <EmptyState
            icon={Newspaper}
            title="No postings"
            description="Create your first posting to get started."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  {['Title', 'Slug', 'Status', 'Published', 'Featured', ''].map((c, i) => (
                    <th
                      key={c || `c-${i}`}
                      scope="col"
                      className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {postings.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        onClick={() => setEditing(p)}
                        className="text-sm font-medium text-gray-900 hover:text-brand-700 text-left"
                      >
                        {p.title}
                      </button>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 font-mono">{p.slug}</td>
                    <td className="px-5 py-4">
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize',
                          STATUS_BADGE[p.status],
                        )}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(p.publishedAt)}
                    </td>
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        aria-label={p.featured ? 'Unfeature' : 'Feature'}
                        onClick={() =>
                          update.mutate({ id: p.id, patch: { featured: !p.featured } })
                        }
                        className={cn(
                          'rounded-md p-1',
                          p.featured ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400',
                        )}
                      >
                        <Star size={18} aria-hidden="true" fill={p.featured ? 'currentColor' : 'none'} />
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {p.status !== PostingStatus.PUBLISHED && (
                          <button
                            type="button"
                            aria-label="Publish"
                            disabled={publish.isPending}
                            onClick={() => publish.mutate(p.id)}
                            className="rounded-md p-1 text-gray-400 hover:bg-green-50 hover:text-green-600"
                          >
                            <Send size={16} aria-hidden="true" />
                          </button>
                        )}
                        {p.status !== PostingStatus.ARCHIVED && (
                          <button
                            type="button"
                            aria-label="Archive"
                            disabled={archive.isPending}
                            onClick={() => archive.mutate(p.id)}
                            className="rounded-md p-1 text-gray-400 hover:bg-amber-50 hover:text-amber-600"
                          >
                            <Archive size={16} aria-hidden="true" />
                          </button>
                        )}
                        <button
                          type="button"
                          aria-label="Delete"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(p.id)}
                          className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && <PostingEditor posting={null} onClose={() => setCreating(false)} />}
      {editing && <PostingEditor posting={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
